import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DRIVER_COMMISSION_LABELS, SELLER_COMMISSION_LABELS, type CommissionReportDTO, type CommissionRowDTO } from '@bifeai/shared';
import { Button, Callout, Dialog, EmptyState, Field, Input, PageHeader, Select, Skeleton, useToast } from '@bifeai/ui';
import { Percent, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import '../orders-chat/orders-chat.css';
import './wholesale.css';
import { moneyInput, parseMoney } from './common';

const iso = (d: Date) => d.toLocaleDateString('sv-SE');
function period(kind: string): { from: string; to: string } {
  const now = new Date();
  if (kind === 'last')
    return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (kind === 'week') {
    const d = new Date(now);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return { from: iso(d), to: iso(now) };
  }
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
}
const pctText = (n: number) => `${n.toLocaleString('es-AR', { maximumFractionDigits: 2 })} %`;

/** Comisiones de vendedores y repartidores del mayorista: lo que le toca a cada uno en el período y lo pagado. */
export function CommissionsPage() {
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const owner = can('settings.manage');
  const [kind, setKind] = useState('month');
  const [custom, setCustom] = useState(period('month'));
  const p = kind === 'custom' ? custom : period(kind);
  const [paying, setPaying] = useState<CommissionRowDTO | null>(null);
  const [editing, setEditing] = useState<CommissionRowDTO | null>(null);
  const q = useQuery({
    queryKey: ['wholesale', 'commissions', p.from, p.to],
    queryFn: () => api.get<CommissionReportDTO>(`/wholesale/commissions?from=${p.from}&to=${p.to}`),
  });
  const r = q.data;
  const none = r && r.sellerCommission === 'NONE' && r.driverCommission === 'NONE';
  return (
    <>
      <PageHeader
        title="Comisiones"
        subtitle="Lo que le corresponde a cada vendedor y repartidor según cómo lo configuró el dueño, lo ya pagado y lo que falta pagar."
      />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <Select value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 'auto' }} aria-label="Período">
          <option value="month">Este mes</option>
          <option value="last">Mes pasado</option>
          <option value="week">Esta semana</option>
          <option value="custom">Elegir fechas</option>
        </Select>
        {kind === 'custom' && (
          <>
            <Input
              type="date"
              value={custom.from}
              onChange={(e) => setCustom({ ...custom, from: e.target.value })}
              style={{ width: 'auto' }}
              aria-label="Desde"
            />
            <Input
              type="date"
              value={custom.to}
              onChange={(e) => setCustom({ ...custom, to: e.target.value })}
              style={{ width: 'auto' }}
              aria-label="Hasta"
            />
          </>
        )}
        <span className="bf-cell-sub">
          {formatDate(p.from)} a {formatDate(p.to)}
        </span>
      </div>
      {!r ? (
        <Skeleton style={{ height: 200 }} />
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          <Callout tone={none ? 'warning' : 'info'}>
            Vendedor: <strong>{SELLER_COMMISSION_LABELS[r.sellerCommission]}</strong> · Repartidor:{' '}
            <strong>{DRIVER_COMMISSION_LABELS[r.driverCommission]}</strong>.{' '}
            {owner ? <Link to="/mayorista/configuracion">Cambiar en Configurar mayorista</Link> : 'Lo configura el dueño.'}
          </Callout>
          {r.rows.length === 0 ? (
            <EmptyState icon={<Percent />} title="Sin vendedores ni repartidores" text="Dales el permiso de mayorista en Usuarios." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Persona</th>
                    {r.sellerCommission !== 'NONE' && (
                      <th style={{ textAlign: 'right' }}>{r.sellerCommission === 'COLLECTED' ? 'Cobrado' : 'Vendido'}</th>
                    )}
                    {r.sellerCommission !== 'NONE' && <th style={{ textAlign: 'right' }}>Como vendedor</th>}
                    {r.driverCommission !== 'NONE' && <th style={{ textAlign: 'right' }}>Entregas</th>}
                    {r.driverCommission !== 'NONE' && <th style={{ textAlign: 'right' }}>Como repartidor</th>}
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Pagado</th>
                    <th style={{ textAlign: 'right' }}>A pagar</th>
                    {manage && <th />}
                  </tr>
                </thead>
                <tbody>
                  {r.rows.map((x) => (
                    <tr key={x.userId}>
                      <td>
                        <div>{x.name}</div>
                        <div className="bf-cell-sub">
                          {[
                            r.sellerCommission !== 'NONE' && `${pctText(x.sellerPct)}${x.own.sellerPct !== null ? ' (propio)' : ''}`,
                            (r.driverCommission === 'FIXED' || r.driverCommission === 'BOTH') &&
                              `${formatMoney(x.driverFixedCents)} por entrega${x.own.driverFixedCents !== null ? ' (propio)' : ''}`,
                            (r.driverCommission === 'PERCENT' || r.driverCommission === 'BOTH') &&
                              `${pctText(x.driverPct)} de lo entregado${x.own.driverPct !== null ? ' (propio)' : ''}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      {r.sellerCommission !== 'NONE' && <td style={{ textAlign: 'right' }}>{formatMoney(x.sellerBaseCents)}</td>}
                      {r.sellerCommission !== 'NONE' && <td style={{ textAlign: 'right' }}>{formatMoney(x.sellerCents)}</td>}
                      {r.driverCommission !== 'NONE' && (
                        <td style={{ textAlign: 'right' }}>
                          <div>{x.deliveries}</div>
                          <div className="bf-cell-sub">{formatMoney(x.deliveredCents)}</div>
                        </td>
                      )}
                      {r.driverCommission !== 'NONE' && <td style={{ textAlign: 'right' }}>{formatMoney(x.driverCents)}</td>}
                      <td style={{ textAlign: 'right' }}>
                        <strong>{formatMoney(x.totalCents)}</strong>
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(x.paidCents)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <strong>{formatMoney(x.dueCents)}</strong>
                      </td>
                      {manage && (
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {owner && (
                            <Button size="sm" variant="ghost" onClick={() => setEditing(x)}>
                              %
                            </Button>
                          )}
                          <Button size="sm" disabled={x.dueCents <= 0} onClick={() => setPaying(x)}>
                            Pagar
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {r.payouts.length > 0 && <Payouts r={r} manage={manage} />}
        </div>
      )}
      {paying && <PayDialog row={paying} from={p.from} to={p.to} onClose={() => setPaying(null)} />}
      {editing && <RateDialog row={editing} report={r!} onClose={() => setEditing(null)} />}
    </>
  );
}

function Payouts({ r, manage }: { r: CommissionReportDTO; manage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/wholesale/commissions/payouts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wholesale'] }),
    onError: (e) => toast.error('No se pudo anular', errorMessage(e)),
  });
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <strong>Pagos del período</strong>
      <div className="bf-table-wrap">
        <table className="bf-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Persona</th>
              <th>Período</th>
              <th style={{ textAlign: 'right' }}>Importe</th>
              <th>Notas</th>
              {manage && <th />}
            </tr>
          </thead>
          <tbody>
            {r.payouts.map((p) => (
              <tr key={p.id}>
                <td>{formatDate(p.createdAt)}</td>
                <td>{p.name}</td>
                <td>
                  {formatDate(p.from)} a {formatDate(p.to)}
                </td>
                <td style={{ textAlign: 'right' }}>{formatMoney(p.amountCents)}</td>
                <td>
                  {p.notes ?? ''}
                  {p.fromCash && <div className="bf-cell-sub">Salió de la caja mayorista</div>}
                </td>
                {manage && (
                  <td>
                    <button
                      type="button"
                      className="bf-order-del"
                      aria-label="Anular pago"
                      onClick={() => window.confirm('¿Anular este pago? Si salió de la caja mayorista, la plata vuelve.') && del.mutate(p.id)}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayDialog({ row, from, to, onClose }: { row: CommissionRowDTO; from: string; to: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState(moneyInput(row.dueCents));
  const [fromCash, setFromCash] = useState(false);
  const [notes, setNotes] = useState('');
  const cents = parseMoney(amount);
  const pay = useMutation({
    mutationFn: () =>
      api.post('/wholesale/commissions/payouts', { userId: row.userId, from, to, amountCents: cents, fromCash, notes: notes || null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success('Pago registrado', `${row.name} · ${formatMoney(cents ?? 0)}`);
      onClose();
    },
    onError: (e) => toast.error('No se pudo registrar', errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Pagar comisión a ${row.name}`}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div className="bf-cell-sub">
          Le corresponden {formatMoney(row.totalCents)} del {formatDate(from)} al {formatDate(to)}; ya se le pagó {formatMoney(row.paidCents)}.
        </div>
        <Field label="Importe">
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" autoFocus />
        </Field>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={fromCash} onChange={(e) => setFromCash(e.target.checked)} /> Sale en efectivo de la caja mayorista
        </label>
        <Field label="Notas (opcional)">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej.: transferencia del viernes" />
        </Field>
        <Button variant="primary" block loading={pay.isPending} disabled={!cents || cents <= 0} onClick={() => pay.mutate()}>
          Registrar pago
        </Button>
      </div>
    </Dialog>
  );
}

function RateDialog({ row, report, onClose }: { row: CommissionRowDTO; report: CommissionReportDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const num = (v: number | null) => (v === null ? '' : String(v).replace('.', ','));
  const [seller, setSeller] = useState(num(row.own.sellerPct));
  const [fixed, setFixed] = useState(moneyInput(row.own.driverFixedCents));
  const [driver, setDriver] = useState(num(row.own.driverPct));
  const pct = (v: string) => (v.trim() ? Math.max(0, Math.min(100, Number(v.replace(',', '.')) || 0)) : null);
  const save = useMutation({
    mutationFn: () =>
      api.put(`/wholesale/commissions/rates/${row.userId}`, {
        sellerPct: pct(seller),
        driverFixedCents: fixed.trim() ? (parseMoney(fixed) ?? 0) : null,
        driverPct: pct(driver),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success('Comisión guardada', row.name);
      onClose();
    },
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Comisión de ${row.name}`}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div className="bf-cell-sub">Vacío = la general de Configurar mayorista.</div>
        {report.sellerCommission !== 'NONE' && (
          <Field label="% como vendedor">
            <Input value={seller} onChange={(e) => setSeller(e.target.value)} inputMode="decimal" placeholder="General" />
          </Field>
        )}
        {(report.driverCommission === 'FIXED' || report.driverCommission === 'BOTH') && (
          <Field label="Por cada entrega">
            <Input value={fixed} onChange={(e) => setFixed(e.target.value)} inputMode="decimal" placeholder="General" />
          </Field>
        )}
        {(report.driverCommission === 'PERCENT' || report.driverCommission === 'BOTH') && (
          <Field label="% de lo entregado">
            <Input value={driver} onChange={(e) => setDriver(e.target.value)} inputMode="decimal" placeholder="General" />
          </Field>
        )}
        <Button variant="primary" block loading={save.isPending} onClick={() => save.mutate()}>
          Guardar
        </Button>
      </div>
    </Dialog>
  );
}
